import type { Id } from '@/convex/_generated/dataModel';
import { isSendAccepted } from '@/components/org-chat/org-chat-delivery';
import { createOrgChatClientInputId } from '@/components/org-chat/org-chat-payload-builders';
import type {
  RealtimeVoiceDelegationPresetData,
  RealtimeVoiceDelegationTarget,
} from '@/lib/realtime-voice-agent';

export type RealtimeVoiceSourceThread = {
  _id: Id<'orgThreads'>;
  organizationId: Id<'organizations'>;
  status?: string;
  threadStatus?: string;
  updatedAt: number;
  activePromptMessageId?: string;
  activeResponseWorkflowId?: string;
};

type VoiceMessageOptions = { realtimeVoiceSessionId?: string };

type VoiceMessageInput = {
  orgThreadId: Id<'orgThreads'>;
  orgId: Id<'organizations'>;
  prompt: string;
  rawUserPrompt?: string;
  forceDeepResearch?: boolean;
  realtimeVoiceSessionId?: string;
};

type ThreadVoiceDispatchDependencies = {
  orgThreadId: Id<'orgThreads'>;
  orgId: Id<'organizations'>;
  getThread: () => RealtimeVoiceSourceThread | null | undefined;
  send: (
    args: VoiceMessageInput & {
      deliveryMetadata: {
        deliveryKind: 'send';
        clientInputId: string;
        createdAt: number;
      };
    }
  ) => Promise<unknown>;
  enqueue: (args: VoiceMessageInput) => Promise<unknown>;
  stop: (args: {
    orgThreadId: Id<'orgThreads'>;
    expectedPromptMessageId: string;
    expectedWorkflowId: string;
  }) => Promise<boolean>;
};

/** A voice call's original thread remains the destination after its page unmounts. */
export function createRealtimeVoiceThreadDispatch({
  orgThreadId,
  orgId,
  getThread,
  send,
  enqueue,
  stop,
}: ThreadVoiceDispatchDependencies): RealtimeVoiceDelegationTarget {
  let sendInFlight = false;
  let awaitingThreadUpdateAfterSend: number | null = null;

  const requireSourceThread = () => {
    const thread = getThread();
    if (
      !thread ||
      thread._id !== orgThreadId ||
      thread.organizationId !== orgId ||
      thread.status === 'archived'
    ) {
      throw new Error('Voice chat is unavailable or access was lost');
    }
    return thread;
  };

  const isBusy = () => {
    const thread = getThread();
    if (
      !thread ||
      thread._id !== orgThreadId ||
      thread.organizationId !== orgId ||
      thread.status === 'archived'
    ) {
      return false;
    }
    if (
      awaitingThreadUpdateAfterSend !== null &&
      thread.updatedAt > awaitingThreadUpdateAfterSend
    ) {
      awaitingThreadUpdateAfterSend = null;
    }
    return (
      sendInFlight ||
      awaitingThreadUpdateAfterSend !== null ||
      thread.threadStatus === 'pending' ||
      thread.threadStatus === 'processing'
    );
  };

  const messageInput = (
    prompt: string,
    presetData?: RealtimeVoiceDelegationPresetData,
    rawUserPrompt?: string,
    options?: VoiceMessageOptions
  ): VoiceMessageInput => {
    requireSourceThread();
    return {
      orgThreadId,
      orgId,
      prompt,
      ...(rawUserPrompt !== undefined ? { rawUserPrompt } : {}),
      ...(presetData?.forceDeepResearch ? { forceDeepResearch: true } : {}),
      ...(options?.realtimeVoiceSessionId
        ? { realtimeVoiceSessionId: options.realtimeVoiceSessionId }
        : {}),
    };
  };

  const sendNow: NonNullable<
    RealtimeVoiceDelegationTarget['sendMessageNow']
  > = async (prompt, _attachments, presetData, rawUserPrompt, options) => {
    const thread = requireSourceThread();
    const input = messageInput(prompt, presetData, rawUserPrompt, options);
    sendInFlight = true;
    try {
      const result = await send({
        ...input,
        deliveryMetadata: {
          deliveryKind: 'send',
          clientInputId: createOrgChatClientInputId(),
          createdAt: Date.now(),
        },
      });
      const accepted = isSendAccepted(result);
      if (accepted) awaitingThreadUpdateAfterSend = thread.updatedAt;
      return { status: 'started', accepted };
    } finally {
      sendInFlight = false;
    }
  };

  const queue: NonNullable<
    RealtimeVoiceDelegationTarget['enqueueMessage']
  > = async (
    prompt,
    _attachments,
    presetData,
    rawUserPrompt,
    _queueId,
    options
  ) => {
    await enqueue(messageInput(prompt, presetData, rawUserPrompt, options));
  };

  return {
    get pending() {
      return isBusy();
    },
    sendMessage: sendNow,
    sendMessageNow: sendNow,
    enqueueMessage: queue,
    dispatchMessage: async (
      prompt,
      _attachments,
      presetData,
      rawUserPrompt,
      options
    ) => {
      if (isBusy()) {
        await queue(
          prompt,
          undefined,
          presetData,
          rawUserPrompt,
          undefined,
          options
        );
        return { status: 'queued', accepted: true };
      }
      return sendNow(prompt, undefined, presetData, rawUserPrompt, options);
    },
    stopStreaming: async () => {
      const thread = requireSourceThread();
      if (
        !isBusy() ||
        !thread.activePromptMessageId ||
        !thread.activeResponseWorkflowId
      ) {
        return false;
      }
      return await stop({
        orgThreadId,
        expectedPromptMessageId: thread.activePromptMessageId,
        expectedWorkflowId: thread.activeResponseWorkflowId,
      });
    },
  };
}
